import { create } from 'zustand';
import { contextApi } from '../services/api';
import type { ActiveContext, ContextPatch } from '../services/api';

/**
 * ActiveContext store (KB Doc 142). Mirrors the journal's authoritative scope
 * for the ActiveContextWidget. The application owns scope — this store reads it
 * and lets the user switch/re-resolve it explicitly.
 */
interface ActiveContextStore {
  journalId: string | null;
  context: ActiveContext | null;
  loading: boolean;
  error: string | null;

  load: (journalId: string) => Promise<void>;
  setScope: (patch: ContextPatch, reason?: string) => Promise<void>;
  reResolve: () => Promise<void>;
  clear: () => void;
}

export const useActiveContext = create<ActiveContextStore>((set, get) => ({
  journalId: null,
  context: null,
  loading: false,
  error: null,

  load: async (journalId) => {
    set({ journalId, loading: true, error: null });
    try {
      const context = await contextApi.get(journalId);
      set({ context, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  },

  setScope: async (patch, reason) => {
    const journalId = get().journalId;
    if (!journalId) return;
    set({ loading: true, error: null });
    try {
      const context = await contextApi.set(journalId, patch, reason);
      set({ context, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  },

  reResolve: async () => {
    const journalId = get().journalId;
    if (!journalId) return;
    set({ loading: true, error: null });
    try {
      const context = await contextApi.resolve(journalId);
      set({ context, loading: false });
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : String(err) });
    }
  },

  clear: () => set({ journalId: null, context: null, error: null }),
}));
